import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Zap, Lock, CheckCircle2, XCircle, Loader2, Flame, Lightbulb } from "lucide-react";
import { toast } from "sonner";

const TYPE_LABEL = { fix_sentence: "Fix the sentence", multiple_choice: "Pick the natural one", type_natural: "Say it naturally" };

export default function Drills() {
  const [state, setState] = useState({ loading: true, drill: null, locked: false, reason: null, error: null });
  const [hist, setHist] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    api.get("/drills/today")
      .then(({ data }) => setState({ loading: false, drill: data.drill, locked: !!data.locked, reason: data.reason || null, error: null }))
      .catch((err) => setState({ loading: false, drill: null, locked: false, reason: null, error: formatApiErrorDetail(err?.response?.data?.detail) }));
    api.get("/drills/history").then(({ data }) => setHist(data));
  };
  useEffect(load, []);

  const submit = async () => {
    const d = state.drill;
    const payload = d.items.map((it, i) => it.type === "multiple_choice"
      ? { index: i, option_index: answers[i] ?? null }
      : { index: i, answer: answers[i] ?? "" });
    setSubmitting(true);
    try {
      const { data } = await api.post(`/drills/${d.id}/submit`, { answers: payload });
      setState((s) => ({ ...s, drill: data.drill }));
      api.get("/drills/history").then(({ data }) => setHist(data));
      toast.success(`Drill complete: ${data.drill.score}/${data.drill.items.length}`);
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
    finally { setSubmitting(false); }
  };

  const { loading, drill, locked, reason, error } = state;
  const answered = drill ? drill.items.every((it, i) => it.type === "multiple_choice" ? answers[i] != null : (answers[i] || "").trim()) : false;

  return (
    <div className="space-y-6 max-w-3xl">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <span className="rle-eyebrow">Daily drill</span>
          <h1 className="mt-1 font-display font-extrabold text-3xl text-slate-900">{drill?.title || "Today's 2-minute drill"}</h1>
          <p className="text-slate-600 mt-1">Short fix-it exercises built from your own Mistake Bank.</p>
        </div>
        {hist && <div className="rle-card px-4 py-3 flex items-center gap-2" data-testid="drill-streak"><Flame size={18} className="text-amber-500"/><div><div className="font-display font-bold text-slate-900 leading-tight">{hist.streak} day{hist.streak === 1 ? "" : "s"}</div><div className="text-xs text-slate-500">streak</div></div></div>}
      </header>

      {loading && <div className="rle-card p-8 text-slate-600 flex items-center gap-2" data-testid="drill-loading"><Loader2 size={18} className="animate-spin"/>Building today's drill from your mistakes…</div>}

      {!loading && error && (
        <div className="rle-card p-8 text-slate-700" data-testid="drill-error">{error}<div className="mt-3"><Button onClick={load} variant="outline" className="rounded-full">Try again</Button></div></div>
      )}

      {!loading && !error && locked && (
        <div className="rle-card p-8 text-center" data-testid="drill-locked">
          <Lock size={28} className="mx-auto text-amber-600"/>
          <h2 className="mt-3 font-display font-bold text-xl text-slate-900">Daily drills are a paid feature</h2>
          <p className="text-slate-600 mt-2">{reason}</p>
          <Link to="/pricing"><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 mt-4" data-testid="drill-upgrade-btn">See plans</Button></Link>
        </div>
      )}

      {!loading && !error && !locked && !drill && (
        <div className="rle-card p-8 text-center" data-testid="drill-empty">
          <Zap size={28} className="mx-auto text-emerald-600"/>
          <p className="text-slate-600 mt-3">{reason}</p>
          <Link to="/missions"><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800 mt-4" data-testid="drill-go-missions-btn">Do a mission</Button></Link>
        </div>
      )}

      {!loading && drill && (
        <div className="space-y-4" data-testid="drill-items">
          {drill.status === "completed" && <ScoreCard drill={drill}/>}
          {drill.items.map((it, i) => (
            <DrillItem key={i} index={i} item={it} value={answers[i]} result={drill.results?.[i]} done={drill.status === "completed"}
              onChange={(v) => setAnswers((a) => ({ ...a, [i]: v }))}/>
          ))}
          {drill.status !== "completed" ? (
            <Button onClick={submit} disabled={!answered || submitting} className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="drill-submit-btn">
              {submitting ? <Loader2 size={16} className="mr-1 animate-spin"/> : <CheckCircle2 size={16} className="mr-1"/>}{submitting ? "Checking…" : "Check my answers"}
            </Button>
          ) : (
            <div className="flex flex-wrap gap-2">
              <Link to="/mistakes"><Button variant="outline" className="rounded-full" data-testid="drill-open-mistakes-btn">Open Mistake Bank</Button></Link>
              <Link to="/missions"><Button className="rounded-full bg-emerald-700 hover:bg-emerald-800" data-testid="drill-go-missions-btn">Practise a mission</Button></Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScoreCard({ drill }) {
  const total = drill.items.length; const pct = total ? Math.round((drill.score / total) * 100) : 0;
  return (
    <div className="rounded-2xl p-6 bg-emerald-700 text-white shadow-sm" data-testid="drill-score">
      <div className="text-xs uppercase tracking-widest text-emerald-100">Done for today</div>
      <div className="mt-1 font-display font-extrabold text-3xl" data-testid="drill-score-text">{drill.score} / {total} correct</div>
      <p className="text-emerald-100 mt-1 text-sm">{pct >= 80 ? "Excellent — those corrections are sticking." : pct >= 50 ? "Good progress. Items you missed go back into rotation." : "Keep at it — you'll see these again tomorrow."} Come back tomorrow for a fresh set.</p>
    </div>
  );
}

function DrillItem({ index, item, value, result, done, onChange }) {
  const ok = result?.correct;
  const border = done ? (ok ? "border-emerald-300" : "border-red-200") : "border-slate-200";
  return (
    <div className={`rle-card p-5 ${border}`} data-testid={`drill-item-${index}`}>
      <div className="flex items-center justify-between">
        <span className="rle-eyebrow">{index + 1}. {TYPE_LABEL[item.type]}</span>
        {done && (ok ? <CheckCircle2 size={18} className="text-emerald-600" data-testid={`drill-item-${index}-correct`}/> : <XCircle size={18} className="text-red-500" data-testid={`drill-item-${index}-wrong`}/>)}
      </div>
      <p className="mt-2 text-slate-800">{item.prompt}</p>
      {item.sentence && <p className="mt-1 text-slate-900 font-medium bg-slate-50 border border-slate-200 rounded-xl px-3 py-2">“{item.sentence}”</p>}

      {item.type === "multiple_choice" ? (
        <div className="mt-3 space-y-2">
          {item.options.map((opt, oi) => {
            const chosen = (done ? result?.learner : value) === oi;
            const isAnswer = done && item.answer_index === oi;
            const cls = isAnswer ? "border-emerald-500 bg-emerald-50" : chosen && done ? "border-red-300 bg-red-50" : chosen ? "border-emerald-500 bg-emerald-50" : "border-slate-200 hover:border-slate-300";
            return (
              <button key={oi} type="button" disabled={done} onClick={() => onChange(oi)} className={`w-full text-left px-3 py-2 rounded-xl border text-sm text-slate-800 transition-colors ${cls}`} data-testid={`drill-item-${index}-option-${oi}`}>
                {opt}
              </button>
            );
          })}
        </div>
      ) : (
        <Input value={done ? (result?.learner ?? "") : (value || "")} disabled={done} onChange={(e) => onChange(e.target.value)} placeholder="Type your answer…" className="mt-3 rounded-xl" data-testid={`drill-item-${index}-input`}/>
      )}

      {!done && item.hint && <div className="mt-2 text-xs text-slate-500 flex items-center gap-1"><Lightbulb size={12}/>{item.hint}</div>}
      {done && (
        <div className="mt-3 text-sm space-y-1" data-testid={`drill-item-${index}-feedback`}>
          {item.answer && !ok && <div><span className="text-slate-500 mr-2">Natural version:</span><span className="text-emerald-700 font-semibold">{item.answer}</span></div>}
          {result?.feedback && <div className="text-slate-600">{result.feedback}</div>}
        </div>
      )}
    </div>
  );
}
